import React, { useState } from "react";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import MainCalculatorsList from "./MainCalculators";

const TITLE = "Calculatrice du point de rosée - Calculez la température de saturation de l'humidité de l'air";
const DESCRIPTION =
  "Calculatrice du point de rosée - Calculez la température de saturation de l'humidité de l'air. Calculateur de chimie en francais pour cours et TP, avec saisie simple, resultat rapide et interpretation claire.";
const KEYWORDS =
  "dew point calculator, calculatrice chimie, formule de chimie, tp de chimie, calcul en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/dew-point-calculator/";

const DewPointCalculator = () => {
  const [airTemperature, setAirTemperature] = useState("");
  const [humidity, setHumidity] = useState("");
  const [dewPoint, setDewPoint] = useState("");
  const [unit, setUnit] = useState("Celsius");
  const [dewPointUnit, setDewPointUnit] = useState("Celsius");
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);

  const calculateSaturationVaporPressure = (temp) => {
    return 610.78 * Math.exp((17.27 * temp) / (temp + 237.3));
  };

  const calculateVaporPressure = (saturationPressure, relativeHumidity) => {
    return (relativeHumidity / 100) * saturationPressure;
  };

  const calculateAbsoluteHumidity = (vaporPressure, temp) => {
    return (2.16679 * vaporPressure) / (temp + 273.15);
  };

  const calculateMoistureVolumeConcentration = (vaporPressure) => {
    const atmosphericPressure = 101325;
    return (vaporPressure / atmosphericPressure) * 1e6;
  };

  const calculateMoistureWeightConcentration = (absoluteHumidity) => {
    const airDensity = 1.225;
    return (absoluteHumidity / airDensity) * 1e6;
  };

  const calculateDewPoint = (airTemp, relativeHumidity) => {
    const temp =
      unit === "Celsius"
        ? airTemp
        : unit === "Fahrenheit"
        ? (airTemp - 32) * (5 / 9)
        : airTemp - 273.15;
    const a = 17.27;
    const b = 237.7;
    const alpha = (a * temp) / (b + temp) + Math.log(relativeHumidity / 100);
    return (b * alpha) / (a - alpha);
  };

  const calculateHumidity = (airTemp, dewPointTemp) => {
    const saturationVaporPressure = calculateSaturationVaporPressure(airTemp);
    const vaporPressure = calculateSaturationVaporPressure(dewPointTemp);
    return (vaporPressure / saturationVaporPressure) * 100;
  };

  const handleCalculate = () => {
    try {
      const airTemp = parseFloat(airTemperature);
      const relativeHumidity = parseFloat(humidity);
      const dewPointTemp = parseFloat(dewPoint);

      if (airTemperature && humidity) {
        const calculatedDewPoint = calculateDewPoint(airTemp, relativeHumidity);
        setDewPoint(calculatedDewPoint.toFixed(2));
      } else if (airTemperature && dewPoint) {
        if (airTemp <= dewPointTemp) {
          throw new Error(
            "La température de l'air doit être supérieure à la température du point de rosée."
          );
        }
        const calculatedHumidity = calculateHumidity(airTemp, dewPointTemp);
        setHumidity(calculatedHumidity.toFixed(2));
      } else if (humidity && dewPoint) {
        const temp = dewPointTemp;
        setAirTemperature(temp.toFixed(2));
      } else {
        throw new Error("Veuillez entrer deux valeurs pour calculer la troisième.");
      }

      setError("");

      const saturationVaporPressure = calculateSaturationVaporPressure(airTemp);
      const vaporPressure = calculateVaporPressure(
        saturationVaporPressure,
        relativeHumidity || calculateHumidity(airTemp, dewPointTemp)
      );
      const absoluteHumidity = calculateAbsoluteHumidity(vaporPressure, airTemp);
      const moistureVolumeConcentration =
        calculateMoistureVolumeConcentration(vaporPressure);
      const moistureWeightConcentration =
        calculateMoistureWeightConcentration(absoluteHumidity);

      setResults({
        saturationVaporPressure,
        vaporPressure,
        absoluteHumidity,
        moistureVolumeConcentration,
        moistureWeightConcentration,
      });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>Calculatrice du point de rosée</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  La calculatrice du point de rosée est un outil utile pour déterminer la relation entre la température, l'humidité relative et le point de rosée Cette calculatrice spéciale s'est distinguée en offrant une plus grande flexibilité à l'utilisateur, car il peut entrer deux des trois valeurs mentionnées (température, humidité relative et point de rosée) et calculer automatiquement la troisième valeur. Cette calculatrice se distingue par sa flexibilité et sa facilité d'utilisation, ce qui en fait un outil précieux pour les personnes qui ont besoin de calculer l'une des trois valeurs en fonction des deux valeurs connues qu'elles possèdent. Que vous soyez intéressé par la météorologie, l'agriculture ou tout autre domaine nécessitant une connaissance du point de rosée, cette calculatrice vous sera utile.
                </p>
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>Calculatrice du point de rosée</h2>
                </div>
                <div className="bg-warning text-black text-center">
                  <p>
                    Veuillez entrer <b>seulement deux</b> des trois variables suivantes pour calculer la troisième:
                  </p>
                </div>
                <div dir="ltr">
                  <div>
                    <hr />
                    <div className="form-group">
                      <label className="form-label ms-2">Température de l'air</label>
                      <input
                        type="number"
                        value={airTemperature}
                        onChange={(e) => setAirTemperature(e.target.value)}
                        placeholder="Température"
                        className="form-control"
                        style={{ width: "75%" }}
                      />
                      <select
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        className="form-control mt-2"
                        style={{ width: "75%" }}
                      >
                        <option value="Celsius">Celsius</option>
                        <option value="Fahrenheit">Fahrenheit</option>
                        <option value="Kelvin">Kelvin</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label ms-2">Humidité relative (%)</label>
                      <input
                        type="number"
                        value={humidity}
                        onChange={(e) => setHumidity(e.target.value)}
                        placeholder="Humidité %"
                        className="form-control"
                        style={{ width: "75%" }}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label ms-2">Point de rosée</label>
                      <input
                        type="number"
                        value={dewPoint}
                        onChange={(e) => setDewPoint(e.target.value)}
                        placeholder="Point de rosée"
                        className="form-control"
                        style={{ width: "75%" }}
                      />
                      <select
                        value={dewPointUnit}
                        onChange={(e) => setDewPointUnit(e.target.value)}
                        className="form-control mt-2"
                        style={{ width: "75%" }}
                      >
                        <option value="Celsius">Celsius</option>
                        <option value="Fahrenheit">Fahrenheit</option>
                        <option value="Kelvin">Kelvin</option>
                      </select>
                    </div>
                    <br />
                    <button
                      className="btn btn-success"
                      style={{ fontSize: "20px" }}
                      onClick={handleCalculate}
                    >
                      Calculer
                    </button>
                  </div>
                  <br />
                  {error && (
                    <p className="p-3 mb-2 bg-warning text-black">
                      Erreur: {error}
                    </p>
                  )}
                  {results && (
                    <div className="p-3 mb-2 bg-info text-black">
                      <h4>Résultats:</h4>
                      <p>Pression de vapeur saturante: {results.saturationVaporPressure.toFixed(2)} Pa</p>
                      <p>Pression de vapeur: {results.vaporPressure.toFixed(2)} Pa</p>
                      <p>Humidité absolue: {results.absoluteHumidity.toFixed(4)} g/m³</p>
                      <p>Concentration d'humidité en volume: {results.moistureVolumeConcentration.toFixed(2)} ppm</p>
                      <p>Concentration d'humidité en poids: {results.moistureWeightConcentration.toFixed(2)} µg/m³</p>
                    </div>
                  )}
                  <hr />
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
      </div>
    </div>
  );
};

export default DewPointCalculator;
